import React from "react";
import { Menu, type MenuProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

/**
 * MenuComponent
 * Centralized MUI Menu with consistent Paper styling.
 * Defaults to inherited font (or pass "Source Sans Pro, sans-serif").
 */
// Everything not listed here is forwarded to MUI's `Menu` through `...rest`, so the
// base is `MenuProps`. `placement` / `disableCloseOnMouseLeave` are NOT Menu props -
// KNOWN BUG, preserved verbatim: RowResizeModal passes them and they are forwarded to
// the DOM. They are declared so those call sites keep type-checking.
interface MenuComponentProps extends Omit<MenuProps, "open" | "children"> {
  open: boolean;
  children?: React.ReactNode;
  width?: string | number;
  zIndex?: number;
  fontFamily?: string;
  /**
   * Both are SPREAD into a plain style object below (`listSx` inside the `& .MuiList-root`
   * selector), so they are style objects, not the full `SxProps` union - a callback or array
   * form would spread into nonsense CSS rather than being resolved against the theme.
   */
  paperSx?: SystemStyleObject<Theme>;
  listSx?: SystemStyleObject<Theme>;
  placement?: string;
  disableCloseOnMouseLeave?: boolean;
  [key: `data-${string}`]: string | undefined;
}

function MenuComponent({
  anchorEl,
  open,
  onClose,
  children,
  width = "12.5rem",
  zIndex = 1300,
  fontFamily = "inherit",
  paperSx = {},
  listSx = {},
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  transformOrigin,
  ...rest
}: MenuComponentProps) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{ "& .MuiList-root": { p: 0, ...listSx } }}
      slotProps={{
        paper: {
          sx: {
            border: "1px solid #ECF3FE",
            boxShadow: "0.25rem 0.25rem 0.9375rem rgba(65, 92, 171, 0.15)",
            backgroundColor: "#FFFFFF",
            width,
            zIndex,
            fontFamily,
            "& *": { fontFamily: "inherit" },
            ...paperSx,
          },
        },
      }}
      {...rest}
    >
      {children}
    </Menu>
  );
}

export default MenuComponent;
